/* eslint-disable no-dupe-class-members */
const { isEqual, pick } = require('lodash');

const dayjs = require('../../server/utils/dayjs');
const {
  ENTITY_TYPES,
  ROUTE_RESERVED_KEYWORDS,
  PAGE_CATEGORIES,
  ROUTE_DATE_FORMATS,
  FILTER_TYPES,
} = require('../constants');
const { ROUTE_CONFIGURATIONS } = require('../constants/configurations');
const { getProductionStats, getProductionsCount } = require('../utils/apis');
const getPageEntityInfo = require('../utils/getPageEntityInfo');

const FETCH_FUNCTIONS = {
  GET_PRODUCTION_STATS: getProductionStats,
  GET_PRODUCTIONS_COUNT: getProductionsCount,
};

class PageDetailsResolver {
  #session = null;

  #fetchFunctions = FETCH_FUNCTIONS;

  #previousPageInfo = null;

  #previousFilterParams = null;

  #serverTiming = {
    tickets: [],
    online: [],
    upcoming: [],
  };

  #fetchTime = 0;

  constructor({ session, fetchFunctions, previousPageInfo, previousFilterParams } = {}) {
    if (session) {
      this.#session = session;
    }

    if (fetchFunctions) {
      this.#fetchFunctions = fetchFunctions;
    }

    if (previousPageInfo) {
      this.#previousPageInfo = previousPageInfo;
    }

    if (previousFilterParams) {
      this.#previousFilterParams = previousFilterParams;
    }
  }

  #isSearchable({ basePath }) {
    return !basePath || basePath === ROUTE_RESERVED_KEYWORDS.productions || !ROUTE_CONFIGURATIONS[basePath];
  }

  #isFilterable({ basePath, pageTab, entityType, pro }) {
    if (pro) {
      return (
        !pageTab ||
        [
          ROUTE_RESERVED_KEYWORDS.calendar,
          ROUTE_RESERVED_KEYWORDS['role-graph'],
          ROUTE_RESERVED_KEYWORDS['company-graph'],
        ].includes(pageTab)
      );
    }

    const basePathConfiguration = ROUTE_CONFIGURATIONS[basePath];

    if (basePathConfiguration) {
      return basePathConfiguration?.allowedWithFilters || false;
    }

    if ([ENTITY_TYPES.PROFILE, ENTITY_TYPES.ORGANIZATION].includes(entityType)) {
      return [ROUTE_RESERVED_KEYWORDS.performances].includes(pageTab);
    }

    return false;
  }

  #getPageCategory({ basePath, filters, entityType, entity }) {
    const basePathConfiguration = ROUTE_CONFIGURATIONS[basePath];

    let category = PAGE_CATEGORIES.LANDING;

    if (basePathConfiguration) {
      category = basePathConfiguration?.pageCategory;
    } else if (basePath || Object.keys(filters || {}).length > 0) {
      category = PAGE_CATEGORIES.LISTING;
    } else {
      category = PAGE_CATEGORIES.LANDING;
    }

    if (!!entityType && entity?.id) {
      category = PAGE_CATEGORIES.STUB;
    }

    return {
      category,
    };
  }

  async #fetchPageStats({ filterParams, language }) {
    const relevantFilterParams = pick(filterParams, ['search_exp', 'ctx_entity']);
    let tickets = 0;
    let online = 0;
    let upcoming = 0;

    try {
      const startTime = new Date().getTime();
      const todayObj = dayjs().locale('en');
      const today = todayObj.format(ROUTE_DATE_FORMATS.STANDARD);

      const hasFutureDates =
        (!!filterParams?.date_from &&
          (dayjs(filterParams?.date_from).isAfter(todayObj, 'day') ||
            dayjs(filterParams?.date_from).isSame(todayObj, 'day'))) ||
        (!!filterParams?.date_to && dayjs(filterParams?.date_to).isAfter(todayObj, 'day'));

      const dateFrom =
        filterParams?.date_from &&
        (dayjs(filterParams?.date_from).isAfter(todayObj, 'day') ||
          dayjs(filterParams?.date_from).isSame(todayObj, 'day'))
          ? filterParams?.date_from
          : today;
      const dateTo =
        filterParams?.date_to &&
        (dayjs(filterParams?.date_to).isAfter(dateFrom, 'day') || dayjs(filterParams?.date_to).isSame(dateFrom, 'day'))
          ? filterParams?.date_to
          : null;

      const [ticketsResponse, onlineResponse, upcomingProductionsCountResponse] = await Promise.all([
        this.#fetchFunctions.GET_PRODUCTION_STATS(
          {
            filters: {
              ...relevantFilterParams,
              date_from: today,
              facets: 'has_tickets',
              limit: 1,
            },
            language,
          },
          this.#session,
        ),
        this.#fetchFunctions.GET_PRODUCTION_STATS(
          {
            filters: {
              ...relevantFilterParams,
              facets: 'has_online',
              limit: 1,
            },
            language,
          },
          this.#session,
        ),
        ...(hasFutureDates
          ? [
              this.#fetchFunctions.GET_PRODUCTIONS_COUNT(
                {
                  filters: {
                    ...filterParams,
                    date_from: dateFrom,
                    date_to: dateTo,
                  },
                  language,
                },
                this.#session,
              ),
            ]
          : []),
      ]);

      const endTime = new Date().getTime();
      this.#fetchTime = endTime - startTime;

      if (ticketsResponse?.serverTiming) {
        this.#serverTiming.tickets = ticketsResponse.serverTiming;
      }

      if (onlineResponse?.serverTiming) {
        this.#serverTiming.online = onlineResponse.serverTiming;
      }

      if (upcomingProductionsCountResponse?.serverTiming) {
        this.#serverTiming.productionsCount = upcomingProductionsCountResponse.serverTiming;
      }

      if (ticketsResponse?.meta) {
        tickets = ticketsResponse.meta.has_tickets || 0;
      }

      if (onlineResponse?.meta) {
        online = onlineResponse.meta.has_online || 0;
      }

      if (upcomingProductionsCountResponse?.total) {
        upcoming = upcomingProductionsCountResponse.total;
      }
    } catch (err) {
      console.error(err);
    }

    return {
      search: {
        tickets,
        online,
      },
      filters: {
        upcoming,
      },
    };
  }

  getServerTiming({ processOrder, processName }) {
    const statsServerTimings = Object.keys(this.#serverTiming || {}).reduce((acc, key, statsIndex) => {
      const serverTimings = this.#serverTiming[key].map(timer => ({
        ...timer,
        name: `${processOrder}.${statsIndex + 1}-${processName}-${key}-${timer.name}`,
        description: `Stats API (${key}): ${timer.description}`,
      }));

      return [...acc, ...serverTimings];
    }, []);

    if (statsServerTimings.length === 0) {
      return [];
    }

    return [
      ...statsServerTimings,
      {
        name: `${processOrder}.${statsServerTimings.length + 1}-${processName}-fetch-frontend`,
        description: `Stats API (F): with network lag`,
        duration: this.#fetchTime,
      },
    ];
  }

  async getPageStats(state) {
    const { filterParams, page, language } = state || {};
    const { isListingProductions } = page || {};

    if (!isListingProductions) {
      return null;
    }

    let stats = isEqual(this.#previousFilterParams, filterParams) ? this.#previousPageInfo?.stats : null;

    if (!stats) {
      stats = await this.#fetchPageStats({ filterParams, language });
    }

    return stats;
  }

  #getPageIdentifier({ entityType, entity, basePath, category, pageTab, filters, isListingProductions, url }) {
    let identifier = '';

    if (category === PAGE_CATEGORIES.LISTING) {
      if (entityType) {
        identifier += `${entityType}-`;
      } else {
        identifier += `${basePath}-`;
      }

      identifier += `${category}`;
    } else if (category === PAGE_CATEGORIES.STUB) {
      if (entityType && ![ENTITY_TYPES.PROFILE, ENTITY_TYPES.ORGANIZATION].includes(entityType)) {
        identifier += `${entityType}-${entity?.slug}-${entity?.id}`;
      } else {
        identifier += `${entity?.slug}`;
      }
    } else if (basePath && category === PAGE_CATEGORIES.LANDING) {
      identifier = `${basePath}-${category}`;
    } else {
      identifier = 'homepage';
    }

    const ticketsSuffix =
      isListingProductions && filters?.[FILTER_TYPES.TICKET] ? `-${ROUTE_RESERVED_KEYWORDS.tickets}` : '';
    const watchOnlineSuffix =
      isListingProductions && filters?.[FILTER_TYPES.WATCH_ONLINE] ? `-${ROUTE_RESERVED_KEYWORDS['watch-online']}` : '';
    const rentalsSuffix =
      isListingProductions && filters?.[FILTER_TYPES.RENTALS] ? `-${ROUTE_RESERVED_KEYWORDS.rentals}` : '';
    const archivesSuffix =
      isListingProductions && url?.includes(ROUTE_RESERVED_KEYWORDS.archives)
        ? `-${ROUTE_RESERVED_KEYWORDS.archives}`
        : '';

    const pageTabSuffix = pageTab ? `-${pageTab}` : '';

    return `${identifier}${rentalsSuffix}${watchOnlineSuffix}${ticketsSuffix}${archivesSuffix}${pageTabSuffix}`;
  }

  getDetails(state) {
    const { linkProps, url } = state || {};
    const { edit, pro, tab, filters } = linkProps;
    const basePath = (linkProps?.basePath || '').split('/')?.[0];

    const { entityType, entity, attributes, filterTypeKey } = getPageEntityInfo({
      filters,
      basePath,
      initEntityType: linkProps?.entityType,
      initEntity: linkProps?.entity,
    });

    let pageTab = entity?.id ? tab : null;
    const isRentalsTabOnStubPage =
      entity?.id &&
      [ENTITY_TYPES.PROFILE, ENTITY_TYPES.ORGANIZATION].includes(entityType) &&
      filters?.[FILTER_TYPES.RENTALS];

    if (isRentalsTabOnStubPage) {
      pageTab = ROUTE_RESERVED_KEYWORDS.rentals;
    }

    const searchable = this.#isSearchable({ basePath });
    const filterable = this.#isFilterable({ basePath, pageTab, entityType, pro });
    const { category } = this.#getPageCategory({
      basePath,
      entityType,
      entity,
      filters,
    });

    const isProductionsResultsPage = category === PAGE_CATEGORIES.LISTING && entityType === ENTITY_TYPES.PRODUCTION;
    const isStubPage =
      category === PAGE_CATEGORIES.STUB && [ENTITY_TYPES.ORGANIZATION, ENTITY_TYPES.PROFILE].includes(entityType);
    const isPublicStubPage =
      !pro && isStubPage && [ROUTE_RESERVED_KEYWORDS.performances, ROUTE_RESERVED_KEYWORDS.rentals].includes(pageTab);
    const isProStubPage = pro && isStubPage && (!pageTab || pageTab === ROUTE_RESERVED_KEYWORDS.calendar);

    const isListingProductions = isProductionsResultsPage || isPublicStubPage || isProStubPage;

    return {
      entityType,
      ...(entity?.id && {
        entity,
        ...(attributes && { attributes }),
        edit,
        pro,
        tab: pageTab,
        filterTypeKey,
      }),
      searchable,
      filterable,
      category,
      isListingProductions,
      identifier: this.#getPageIdentifier({
        entityType,
        entity,
        category,
        basePath,
        pageTab,
        isListingProductions,
        filters,
        url,
      }),
    };
  }
}

module.exports = PageDetailsResolver;
